import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Grid, TextField, Button, Paper, Alert, CircularProgress } from '@mui/material';
import axios from 'axios';
import SEO from '../components/SEO';
import { trackEvent } from '../components/GoogleAnalytics';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Track form field interactions
    trackEvent('Form', 'Field Interaction', e.target.name);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    setLoading(true);

    // Track form submission attempt
    trackEvent('Form', 'Submit', 'Contact Form');

    try {
      const response = await axios.post('/api/contact', formData);
      setStatus({ type: 'success', message: response.data.message });
      // Track successful submission
      trackEvent('Form', 'Success', 'Contact Form');
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setStatus({
        type: 'error',
        message: error.response?.data?.message || 'Failed to submit contact details. Please try again.',
      });
      // Track failed submission
      trackEvent('Form', 'Error', 'Contact Form');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <SEO 
        title="Contact SOLID CLASSES - Get in Touch"
        description="Contact SOLID CLASSES for admissions, fee structure, or any other queries. Located in Jodhpur, Rajasthan. Call us at +91 90790-35708 or email at solidclassesjodhpur@gmail.com"
        keywords="contact SOLID CLASSES, SOLID CLASSES contact number, SOLID CLASSES address, SOLID CLASSES Jodhpur contact, admission enquiry"
      />
      <Typography variant="h3" component="h1" gutterBottom align="center">
        Contact Us
      </Typography>
      <Typography variant="h6" color="text.secondary" paragraph align="center" sx={{ mb: 6 }}>
        Get in touch with us for any queries or information
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom>
              Send us a Message
            </Typography>
            {status && (
              <Alert severity={status.type} sx={{ mb: 2 }}>
                {status.message}
              </Alert>
            )}
            <Box component="form" noValidate sx={{ mt: 2 }} onSubmit={handleSubmit}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="name"
                label="Full Name"
                name="name"
                autoComplete="name"
                value={formData.name}
                onChange={handleChange}
                disabled={loading}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                disabled={loading}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="phone"
                label="Phone Number"
                name="phone"
                autoComplete="tel"
                value={formData.phone}
                onChange={handleChange}
                disabled={loading}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="message"
                label="Message"
                name="message"
                multiline
                rows={4}
                value={formData.message}
                onChange={handleChange}
                disabled={loading}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Message'}
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom>
              Contact Information
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" paragraph>
                <strong>Director:</strong><br />
                Kishan Chitara<br />
                7 Years Experience, M.Sc. Physics<br />
                Jodhpur, Rajasthan
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Address:</strong><br />
                1st Floor, Bhati Cement<br />
                Pratap Nagar, Jodhpur<br />
                Rajasthan
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Phone:</strong><br />
                +91 90790-35708<br />
                +91 94608-64098
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Email:</strong><br />
                solidclassesjodhpur@gmail.com<br />
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Working Hours:</strong><br />
                Monday - Saturday: 9:00 AM - 7:00 PM<br />
                Sunday: Closed
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      {/* Google Map Embed */}
      <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center' }}>
        <Box
          sx={{
            width: '100%',
            maxWidth: 600,
            aspectRatio: '16/9',
            boxShadow: 3,
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <iframe
            title="SOLID CLASSES Location"
            src="https://www.google.com/maps?q=26.286139,72.995944&z=17&output=embed"
            width="100%"
            height="100%"
            style={{ border: 0, minHeight: 240 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </Box>
      </Box>
    </Container>
  );
};

export default Contact; 
